import {ButtonGroup, closeCurrentDialog, DataStatus} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {FileResponseBody} from '@dms/api/shared';
import {useCreateComplaintDocumentMutation} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {noop} from 'shared/web';

import {useDocumentTemplates} from '../../../hooks/useDocumentTemplates';
import {handleApiError} from '../../../utils/handleApiError';
import {
  DocumentTemplateBox,
  TemplateBoxTemplate,
} from '../../DocumentTemplateBox/DocumentTemplateBox';

interface ComplaintDocumentDialogProps {
  complaintId: string;
  onDocumentCreation: (document: FileResponseBody) => void;
}

export function ComplaintDocumentDialog(props: ComplaintDocumentDialogProps) {
  const [documentTemplate, setDocumentTemplate] = useState<TemplateBoxTemplate>();

  const [getTemplates, {isLoading: isTemplatesLoading, isError: isTemplatesError}] =
    useDocumentTemplates();
  const {defaultTemplates, selectedTemplates, selectedAuthProfileLabel, primaryTemplate} =
    getTemplates('complaint');

  const [createDocument, {isLoading: isCreateLoading}] = useCreateComplaintDocumentMutation();

  useEffect(() => {
    if (isNotNilOrEmpty(documentTemplate)) {
      return;
    }

    setDocumentTemplate(primaryTemplate);
  }, [documentTemplate, primaryTemplate]);

  const handleSubmit = () =>
    createDocument({body: {complaintId: props.complaintId, templateId: documentTemplate?.id ?? ''}})
      .unwrap()
      .then(props.onDocumentCreation)
      .catch(handleApiError);

  const isLoading = isTemplatesLoading || isCreateLoading;
  const isError = isTemplatesError;
  return (
    <DataStatus isEmpty={isNil(documentTemplate)} isLoading={isLoading} isError={isError}>
      <VStack spacing={4}>
        <Text size="small" color="secondary">
          {i18n.t('entity.vehicleComplaint.labels.complaintDocumentCreation')}
        </Text>
        <DocumentTemplateBox
          title={i18n.t('entity.vehicleComplaint.labels.complaintDocument')}
          templates={defaultTemplates}
          authorizationTemplates={selectedTemplates}
          authorizationProfileLabel={selectedAuthProfileLabel}
          selectedTemplate={documentTemplate}
          onTemplateChange={setDocumentTemplate}
          onSelect={noop}
          isSelected
          isDisabled
        />
        <ButtonGroup
          align="right"
          buttons={[
            {
              title: i18n.t('general.actions.cancel'),
              onClick: closeCurrentDialog,
              type: 'button',
              variant: 'secondary',
            },
            {
              title: i18n.t('general.actions.confirm'),
              onClick: handleSubmit,
              type: 'button',
              isLoading: isCreateLoading,
            },
          ]}
        />
      </VStack>
    </DataStatus>
  );
}
